import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Backdrop from "@mui/material/Backdrop";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import { handleSocialLogin } from "./socialMediaHandlers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import facebookIcon from "assets/images/icons/socials/facebook.png";
import youtubeIcon from "assets/images/icons/socials/youtube.png";
import instagramIcon from "assets/images/icons/socials/instagram.png";
// Add others when needed

const socialPlatforms = [
  { name: "Facebook", icon: facebookIcon, handler: "facebook" },
  { name: "YouTube", icon: youtubeIcon, handler: "youtube" },
  { name: "Instagram", icon: instagramIcon, handler: "instagram" },
];

function ConnectOwnProfile({ open, onClose, flowIntent = "brand" }) {
  const [loading, setLoading] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: "v17.0",
      });
      window.FB.AppEvents.logPageView();
    };

    const loadFacebookSDK = () => {
      if (document.getElementById("facebook-jssdk")) return;
      const js = document.createElement("script");
      js.id = "facebook-jssdk";
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      document.body.appendChild(js);
    };

    loadFacebookSDK();
  }, []);

  useEffect(() => {
    if (open) {
      setShowCard(true);
    } else {
      const timeout = setTimeout(() => setShowCard(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  const handleCloseClick = () => {
    setShowCard(false);
    setTimeout(onClose, 300);
  };

  const handleConnect = async (handlerName) => {
    setLoading(true);
    setErrorMessage("");
    setSelectedPlatform(handlerName);
    try {
      localStorage.setItem("postOAuthIntent", flowIntent); // ✅ use passed-in intent
      localStorage.setItem("postOAuthConfirmed", "true");

      await handleSocialLogin(handlerName); // redirects
    } catch (error) {
      console.error("Error during social login:", error);
      setErrorMessage(error.message || "An error occurred while connecting.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Backdrop
      open={open || showCard}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        transition: "opacity 0.3s ease-in-out",
      }}
    >
      <Slide direction="up" in={showCard} mountOnEnter unmountOnExit>
        <Card
          sx={{
            width: "90%",
            maxWidth: 600,
            p: 3,
            backgroundColor: "white",
            borderRadius: "16px",
            position: "relative",
            boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
          }}
        >
          <IconButton
            onClick={handleCloseClick}
            sx={{ position: "absolute", top: 5, right: 5 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            mb={3}
          >
            Connect Your Profile
          </Typography>

          {errorMessage && (
            <Box mb={2} px={1}>
              <Typography variant="body2" color="error" textAlign="center">
                {errorMessage}
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="200px"
            >
              <CircularProgress color="accent" />
            </Box>
          ) : (
            <MDBox
              sx={{
                borderRadius: "16px",
                backgroundColor: "#f0f2f5",
                padding: "16px",
              }}
            >
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
              >
                {socialPlatforms.map((platform) => {
                  const isSelected = selectedPlatform === platform.handler;

                  return (
                    <Grid item key={platform.name}>
                      <Card
                        onClick={() => handleConnect(platform.handler)}
                        sx={{
                          cursor: "pointer",
                          width: 140,
                          height: 120,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "12px",
                          textAlign: "center",
                          border: isSelected
                            ? "2px solid #00A1A1"
                            : "1px solid #ddd",
                          boxShadow: isSelected ? 6 : 1,
                          backgroundColor: isSelected
                            ? "grey.400"
                            : "grey.100",
                          color: isSelected ? "black" : "text.primary",
                          position: "relative",
                          transition: "all 0.3s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 4,
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={platform.icon}
                          alt={platform.name}
                          sx={{ width: 48, height: 48, mb: 1 }}
                        />
                        <Typography
                          variant="button"
                          fontWeight={isSelected ? "bold" : "medium"}
                          sx={{ fontSize: "0.9rem" }}
                        >
                          {platform.name}
                        </Typography>

                        {/* ✅ Selection check mark */}
                        {isSelected && (
                          <MDBox
                            sx={{
                              position: "absolute",
                              top: 5,
                              right: 5,
                              backgroundColor: "#26282b",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CheckCircleIcon color="accent" sx={{ fontSize: 18 }} />
                          </MDBox>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </MDBox>
          )}
        </Card>
      </Slide>
    </Backdrop>
  );
}

export default ConnectOwnProfile;
